import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Container,
  useTheme,
  alpha,
  IconButton
} from '@mui/material';
import {
  People as PeopleIcon,
  DateRange as DateRangeIcon,
  Assignment as AssignmentIcon,
  BarChart as BarChartIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import AdminLayout from 'pages/admin/components/AdminLayout';
import StatCard from 'pages/admin/components/StatCard';
import RecentActivity from 'pages/admin/components/RecentActivity';
import StudentSignupsChart from 'pages/admin/components/StudentSignupsChart';
import { getDashboardStats } from 'services/dashboardService';
import { useNavigate } from 'react-router-dom';

const AdminDashboardPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeSessions: 0,
    totalProblems: 0,
    totalSubmissions: 0
  });

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const dashboardStats = await getDashboardStats();
      setStats(dashboardStats);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <AdminLayout>
      <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" sx={{ mb: 1, background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'inline-block' }}>
              Dashboard Overview
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome back! Here's what's happening in your league today.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <IconButton onClick={fetchDashboardData} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main' }}>
              <RefreshIcon />
            </IconButton>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/admin/sessions/new')}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                px: 3,
                boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              New Session
            </Button>
          </Box>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={3} sx={{ mb: 4 }} data-demo-id="admin-stats-grid">
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Students"
              value={stats.totalStudents}
              icon={<PeopleIcon />}
              color={theme.palette.success.main}
              onClick={() => navigate('/admin/users')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Sessions"
              value={stats.activeSessions}
              icon={<DateRangeIcon />}
              color={theme.palette.info.main}
              onClick={() => navigate('/admin/sessions')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Problems"
              value={stats.totalProblems}
              icon={<AssignmentIcon />}
              color={theme.palette.warning.main}
              onClick={() => navigate('/admin/problems')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Submissions"
              value={stats.totalSubmissions}
              icon={<BarChartIcon />}
              color={theme.palette.secondary.main}
              onClick={() => navigate('/admin/reports')}
            />
          </Grid>
        </Grid>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Left Column: Charts */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ height: 400, mb: 3 }}>
              <StudentSignupsChart />
            </Box>
            {/* Add more charts or tables here */}
          </Grid>

          {/* Right Column: Activity & Quick Actions */}
          <Grid item xs={12} lg={4}>
            <RecentActivity />
          </Grid>
        </Grid>
      </Container>
    </AdminLayout >
  );
};

export default AdminDashboardPage;